import { BACKEND_URL } from "../config/supabase.js";

// ─── IMAGE COMPRESS BEFORE UPLOAD ────────────────────────────────────────────
// Resizes to max 1200px and re-encodes to JPEG (~85% quality) before uploading.
// Decoding via createImageBitmap() first — it handles HEIC/HEIF (common on Android/iPhone
// cameras) and very large images far more reliably than an <img> tag, which silently fails
// to decode HEIC. Old behaviour shipped the raw multi-MB HEIC on decode failure → the
// upload then dropped on mobile networks with a cryptic "Failed to fetch". Now: always
// produce a small JPEG, or throw a clear, actionable message if the file truly can't decode.
const isHeic = (file) => /image\/hei[cf]/i.test(file.type || "") || /\.(heic|heif)$/i.test(file.name || "");

// Decode a file to an ImageBitmap (or <img>) — createImageBitmap first (broadest native
// support, off-main-thread), <img> as a fallback. Returns null if the browser can't decode.
async function decodeToSource(f) {
  try { return await createImageBitmap(f); } catch (_) {}
  return await new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(f);
    img.onload  = () => { URL.revokeObjectURL(url); resolve(img); };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });
}

// ─── TRANSPARENCY ────────────────────────────────────────────────────────────
// Everything below used to re-encode to JPEG, which has no transparency: a PNG cut-out (logo, sticker, flower…)
// came out with a solid white/black box behind it. If an image really has see-through pixels we now keep it as PNG.
// canvasHasAlpha samples a small copy of the canvas (any pixel that isn't fully opaque counts).
export function canvasHasAlpha(canvas, sample = 256) {
  try {
    const k = Math.min(1, sample / Math.max(canvas.width, canvas.height, 1));
    const w = Math.max(1, Math.round(canvas.width * k)), h = Math.max(1, Math.round(canvas.height * k));
    const c = document.createElement("canvas"); c.width = w; c.height = h;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(canvas, 0, 0, w, h);
    const d = ctx.getImageData(0, 0, w, h).data;
    for (let i = 3; i < d.length; i += 4) if (d[i] < 250) return true;
  } catch (_) { /* tainted / unreadable → treat as opaque (old behaviour) */ }
  return false;
}
// Can this kind of file carry transparency at all? (JPEG/HEIC can't — skip the pixel scan for them.)
export const mayHaveAlpha = (file) => /^image\/(png|webp|avif|gif|svg\+xml)$/i.test(file?.type || "") || /\.(png|webp|avif)$/i.test(file?.name || "");
export async function fileHasAlpha(file) {
  if (!mayHaveAlpha(file)) return false;
  const src = await decodeToSource(file);
  if (!src) return false;
  const c = document.createElement("canvas"); c.width = src.width; c.height = src.height;
  c.getContext("2d").drawImage(src, 0, 0);
  if (src.close) src.close();
  return canvasHasAlpha(c);
}
// Downscale (never up) to maxWidth and keep the see-through pixels → a PNG File.
export async function toTransparentPng(file, maxWidth = 1600, name) {
  const src = await decodeToSource(file);
  if (!src) throw new Error("This image couldn't be processed. Please try another PNG.");
  let w = src.width, h = src.height;
  if (w > maxWidth) { h = Math.round(h * maxWidth / w); w = maxWidth; }
  const c = document.createElement("canvas"); c.width = w; c.height = h;
  c.getContext("2d").drawImage(src, 0, 0, w, h);
  if (src.close) src.close();
  const blob = await new Promise((r) => c.toBlob(r, "image/png"));
  if (!blob) throw new Error("Couldn't process this image. Please try another PNG.");
  return new File([blob], (name || (file.name || "image").replace(/\.\w+$/, "")) + ".png", { type: "image/png" });
}

export async function compressImage(file, maxWidth = 1200, quality = 0.85) {
  // Skip only for non-images and already-small NON-HEIC files (HEIC must always be re-encoded
  // so it uploads small AND displays in browsers, which can't render raw HEIC).
  const looksImage = (file.type || "").startsWith("image/") || isHeic(file);
  if (!looksImage) return file;
  if (file.size < 200 * 1024 && !isHeic(file)) return file;

  // Decode. Handles every common format (JPEG/PNG/WebP/GIF/BMP) + HEIC on Safari/iOS natively.
  // Sellers reported this failing with "couldn't be processed" on the first upload attempt,
  // then succeeding on the exact same photo 2-3 manual retries later — the signature of a
  // TRANSIENT decode failure (createImageBitmap/heic2any under memory or decoder pressure,
  // not an actually-corrupt file), since a genuinely bad file would fail identically every
  // time. Retry internally a few times before surfacing the error, instead of making the
  // seller do that same retrying by hand.
  let source = null;
  for (let attempt = 1; attempt <= 3 && !source; attempt++) {
    source = await decodeToSource(file);

    // HEIC on browsers that can't decode it natively (notably Chrome on Android) → convert
    // with heic2any (WASM), lazy-loaded ONLY when needed, then decode the resulting JPEG.
    if (!source && isHeic(file)) {
      try {
        const heic2any = (await import("heic2any")).default;
        const out = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
        source = await decodeToSource(Array.isArray(out) ? out[0] : out);
      } catch (_) { /* fall through — retried below, or the error below on the last attempt */ }
    }
    if (!source && attempt < 3) await new Promise((r) => setTimeout(r, 400));
  }
  if (!source) {
    throw new Error("This photo couldn't be processed. Please try uploading a JPEG or PNG, or a screenshot of the photo instead.");
  }

  let width  = source.width;
  let height = source.height;
  if (width > maxWidth) { height = Math.round(height * maxWidth / width); width = maxWidth; }
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  canvas.getContext("2d").drawImage(source, 0, 0, width, height);
  if (source.close) source.close();   // free the decoded bitmap

  // A PNG/WebP with see-through pixels stays a PNG (JPEG would paint a solid box behind it).
  if (mayHaveAlpha(file) && canvasHasAlpha(canvas)) {
    const png = await new Promise((r) => canvas.toBlob(r, "image/png"));
    if (png) return new File([png], `${(file.name || "image").replace(/\.\w+$/, "")}.png`, { type: "image/png" });
  }
  const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", quality));
  if (!blob) throw new Error("Couldn't process this photo. Please try a different image or a screenshot.");
  // Re-name to .jpg so the stored file's extension matches its real JPEG contents.
  const baseName = (file.name || "photo").replace(/\.\w+$/, "");
  return new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
}

// Crop an image (data/object URL) to a pixel rectangle → a JPEG File. Used by the
// banner crop tool in Settings so sellers frame their own banner.
export async function getCroppedImg(src, cropPixels) {
  const img = await new Promise((resolve, reject) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = src;
  });
  const w = Math.max(1, Math.round(cropPixels.width));
  const h = Math.max(1, Math.round(cropPixels.height));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  const ctx = canvas.getContext("2d");
  // A see-through PNG stays see-through (no white fill, saved as PNG). Anything else: the part of the frame that
  // extends past the photo (zoomed out / moved off an edge) is white, not black.
  const probe = document.createElement("canvas"); probe.width = img.naturalWidth; probe.height = img.naturalHeight;
  probe.getContext("2d").drawImage(img, 0, 0);
  const keepAlpha = canvasHasAlpha(probe);
  if (!keepAlpha) { ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h); }
  const sx = Math.max(0, cropPixels.x), sy = Math.max(0, cropPixels.y);
  const sw = Math.min(img.naturalWidth, cropPixels.x + cropPixels.width) - sx, sh = Math.min(img.naturalHeight, cropPixels.y + cropPixels.height) - sy;
  if (sw > 0 && sh > 0) {
    const kx = w / cropPixels.width, ky = h / cropPixels.height;
    ctx.drawImage(img, sx, sy, sw, sh, (sx - cropPixels.x) * kx, (sy - cropPixels.y) * ky, sw * kx, sh * ky);
  }
  if (keepAlpha) {
    const png = await new Promise((r) => canvas.toBlob(r, "image/png"));
    return new File([png], "image.png", { type: "image/png" });
  }
  const blob = await new Promise((r) => canvas.toBlob(r, "image/jpeg", 0.9));
  return new File([blob], "banner.jpg", { type: "image/jpeg" });
}

// ─── IMAGE UPLOAD TO SUPABASE STORAGE ────────────────────────────────────────
// Two-tier upload:
//   1. PRIMARY — direct-to-Supabase via a short-lived signed URL (no Render in the
//      byte path → fast + far fewer mobile-network drops, the #1 upload complaint).
//   2. FALLBACK — proxy through Render (the proven path; covers iOS Safari CORS or
//      any direct-upload failure). Uploads never break: if direct fails, we proxy.
// Animated GIFs must be stored byte-for-byte: compressImage() redraws through a canvas and saves a JPEG,
// which keeps only the first frame (the animation is lost).
export const isGifFile = (f) => !!f && ((f.type || "").toLowerCase() === "image/gif" || /\.gif$/i.test(f.name || ""));
export const GIF_MAX_BYTES = 15 * 1024 * 1024; // the Render proxy fallback accepts up to 15 MB

// `raw: true` uploads the file exactly as given (no compression / re-encoding) — used for GIFs.
export async function uploadImageToSupabase(file, userId, { raw = false } = {}) {
  if (!raw) file = await compressImage(file); // compress before upload

  const ext  = (file.name.split(".").pop() || "jpg").toLowerCase().replace(/[^a-z0-9]/g, "") || "jpg";
  const path = `${userId}/${Date.now()}-${Math.random().toString(36).slice(2, 7)}.${ext}`;

  // ── Tier 1: direct-to-Supabase via signed URL ──
  try {
    const sCtrl = new AbortController();
    const sT = setTimeout(() => sCtrl.abort(), 15000);
    const signRes = await fetch(`${BACKEND_URL}/api/signed-upload`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ path }),
      signal: sCtrl.signal,
    });
    clearTimeout(sT);
    if (signRes.ok) {
      const { signedUrl, publicUrl } = await signRes.json();
      if (signedUrl && publicUrl) {
        const uCtrl = new AbortController();
        const uT = setTimeout(() => uCtrl.abort(), 120000);
        const up = await fetch(signedUrl, {
          method: "PUT",
          headers: { "Content-Type": file.type || "image/jpeg", "x-upsert": "true" },
          body: file,
          signal: uCtrl.signal,
        });
        clearTimeout(uT);
        if (up.ok) return publicUrl;
      }
    }
  } catch(_) { /* fall through to the Render proxy */ }

  // ── Tier 2: fallback — proxy through Render ──
  // Wake Render if cold (free/standard sleeps after inactivity), then retry the proxy.
  let serverUp = false;
  const deadline = Date.now() + 90000;
  while (Date.now() < deadline) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 5000);
      const p = await fetch(`${BACKEND_URL}/ping`, { signal: ctrl.signal });
      clearTimeout(t);
      if (p.ok) { serverUp = true; break; }
    } catch(_) {}
    await new Promise(r => setTimeout(r, 3000));
  }
  if (!serverUp) throw new Error("Server could not be reached. Please try again in a moment.");

  let lastErr;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 120000); // 120s upload timeout
      const res = await fetch(`${BACKEND_URL}/upload-image`, {
        method:  "POST",
        headers: { "x-filename": path, "Content-Type": file.type || "image/jpeg" },
        body: file,
        signal: ctrl.signal,
      });
      clearTimeout(t);
      if (!res.ok) {
        const err = await res.text();
        throw new Error(`[${res.status}] ${err}`);
      }
      const { url } = await res.json();
      return url;
    } catch(e) {
      lastErr = e;
      if (attempt < 3) await new Promise(r => setTimeout(r, 2000)); // wait 2s before retry
    }
  }
  throw new Error(`Upload failed after 3 attempts: ${lastErr?.message || lastErr}`);
}

// Uploads a VIDEO via /upload-video (100 MB cap, no compression). Preserves the file
// extension so the returned URL is detected as video by isVideoUrl(). Same bucket as
// images, so videoUrl()/thumbUrl() markers work and it sits alongside product photos.
export async function uploadVideoToSupabase(file, userId) {
  // Wake Render if cold (free tier sleeps) — same pattern as image upload.
  let serverUp = false;
  const deadline = Date.now() + 90000;
  while (Date.now() < deadline) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 5000);
      const p = await fetch(`${BACKEND_URL}/ping`, { signal: ctrl.signal });
      clearTimeout(t);
      if (p.ok) { serverUp = true; break; }
    } catch(_) {}
    await new Promise(r => setTimeout(r, 3000));
  }
  if (!serverUp) throw new Error("Server could not be reached. Please try again in a moment.");

  const ext  = (file.name.split(".").pop() || "mp4").toLowerCase();
  const path = `${userId}/${Date.now()}.${ext}`;
  let lastErr;
  for (let attempt = 1; attempt <= 3; attempt++) {
    try {
      const ctrl = new AbortController();
      const t = setTimeout(() => ctrl.abort(), 180000); // 180s — videos are larger
      const res = await fetch(`${BACKEND_URL}/upload-video`, {
        method:  "POST",
        headers: { "x-filename": path, "Content-Type": file.type || "video/mp4" },
        body: file,
        signal: ctrl.signal,
      });
      clearTimeout(t);
      if (!res.ok) { const err = await res.text(); throw new Error(`[${res.status}] ${err}`); }
      const { url } = await res.json();
      return url;
    } catch(e) {
      lastErr = e;
      if (attempt < 3) await new Promise(r => setTimeout(r, 2000));
    }
  }
  throw new Error(`Video upload failed after 3 attempts: ${lastErr?.message || lastErr}`);
}

// ─── IMAGE TRANSFORM HELPER ───────────────────────────────────────────────────
// Converts Supabase storage URLs to auto-resized thumbnails (Pro feature)
// Falls back to original URL for external images (Cloudinary, etc.)
// Serve images via Bunny CDN — caches Supabase storage at edge, zero egress cost
export function thumbUrl(src, width = 400) {
  if (!src) return "";
  const marker = "/storage/v1/object/public/bloom-images/";
  const idx = src.indexOf(marker);
  if (idx === -1) return src; // not a Supabase storage URL — return as-is
  // skip BunnyCDN rewrite for dev Supabase project — CDN only pulls from prod
  if (src.includes("lspcdknqxsgetxcnesen")) return src;
  const path = src.slice(idx + marker.length);
  return `https://bloom-images.b-cdn.net/${path}?width=${width}&quality=82`;
}

// ─── VIDEO HELPERS (mirror the iOS app) ───────────────────────────────────────
// The app stores video URLs in store_logo_video, store_banner, and among a
// product's images. Detect by extension — tolerating query strings (signed URLs)
// and HLS .m3u8. NOTE: the old regex required the URL to END with the extension,
// so any video URL with a ?query was misdetected as an image and broke.
export const isVideoUrl = (u) => /\.(mp4|mov|webm|m3u8)(\?|$)/i.test(u || "");

// CDN rewrite for video — same Bunny marker as thumbUrl() but with NO ?width/quality
// params (those break video). Dev-Supabase URLs pass through untouched (CDN only
// pulls from prod), mirroring thumbUrl()'s behaviour. NEVER pass a video to thumbUrl().
export function videoUrl(src) {
  if (!src) return null;
  const marker = "/storage/v1/object/public/bloom-images/";
  const idx = (src || "").indexOf(marker);
  if (idx === -1) return src;
  if (src.includes("lspcdknqxsgetxcnesen")) return src; // dev project — no CDN
  return `https://bloom-images.b-cdn.net/${src.slice(idx + marker.length)}`;
}

// Poster = first non-video media; falls back to first media. For a still thumbnail.
export const posterOf = (p) => {
  const media = [...(p?.images || []), p?.img].filter(Boolean);
  return media.find((m) => !isVideoUrl(m)) || media[0] || null;
};
// The seller chose a video cover when the FIRST media item is a video.
export const coverIsVideo = (p) => {
  const media = [...(p?.images || []), p?.img].filter(Boolean);
  return media[0] ? isVideoUrl(media[0]) : false;
};
export const coverVideo = (p) =>
  coverIsVideo(p) ? [...(p.images || []), p.img].filter(Boolean)[0] : null;
